//
// Copyright 2026 DXOS.org
//

import { createTreeCollection } from '@ark-ui/react/collection';
import * as Atom from 'effect/reactivity/Atom';

import { Path } from '../../util/index.ts';
import { type TreeItemDataProps, type TreeModel } from './tree-model.ts';

/**
 * One node handed to zag's tree collection; only nodes on an open path are ever built. A group header
 * (`disposition: 'group'`) is a row but never a collection node: its children are spliced into its parent's.
 */
export type TreeNode<T extends { id: string } = any> = {
  id: string;
  /** Machine value: the joined path, so the same item at two paths keeps independent state. */
  value: string;
  path: string[];
  /** 1-based depth, which is what zag reports as `aria-level`. */
  depth: number;
  /** Absent only on the synthetic root, which zag needs but never renders. */
  item?: T;
  props: TreeItemDataProps;
  branch: boolean;
  /** An open branch with no children: its caret stays (to close it) but reads as empty. */
  empty: boolean;
  open: boolean;
  current: boolean;
  /** A section header (`disposition: 'group'`): rendered as a row, skipped by the keyboard, never selected. */
  group: boolean;
  /** Last of its parent's children in the collection, which offers the drop hitbox's `reparent` zones. */
  last: boolean;
  /** Read by zag's `getNodeState` from the node itself (it ignores `isNodeDisabled` there). */
  disabled: boolean;
  /** Built only while the branch is open; a closed branch is marked by `childrenCount` alone. */
  children?: TreeNode<T>[];
  /** Makes zag's `isBranchNode` true for a closed branch whose children were never read. */
  childrenCount?: number;
  indexPath: number[];
};

export type TreeWalk<T extends { id: string } = any> = {
  root: TreeNode<T>;
  /** Pre-order rows under open branches: zag's `visibleNodes` plus group headers, and what the window slices. */
  rows: TreeNode<T>[];
  /** Each row's index in `rows`. */
  rowIndex: Map<string, number>;
  expanded: string[];
  selected: string[];
  byValue: Map<string, TreeNode<T>>;
};

/**
 * Reactive walk from `TreeModel` atoms to the nodes zag needs. A closed branch contributes its own row and
 * `childrenCount` (from `props.parentOf`) but never reads `childIds` of its subtree, so the cost of a walk is the
 * number of visible rows, not the size of the tree; opening a branch changes `itemOpen`, which re-runs the walk.
 */
export const createTreeWalkAtom = <T extends { id: string }>(
  model: TreeModel<T>,
  rootId: string | undefined,
  rootPath: string[],
): Atom.Atom<TreeWalk<T>> =>
  Atom.make((get): TreeWalk<T> => {
    const rows: TreeNode<T>[] = [];
    const expanded: string[] = [];
    const selected: string[] = [];
    const byValue = new Map<string, TreeNode<T>>();

    /** Appends `parentId`'s children to `siblings`; a group's children join the same list at the same depth. */
    const walk = (
      parentId: string | undefined,
      parentPath: string[],
      parentIndexPath: number[],
      siblings: TreeNode<T>[],
    ): void => {
      for (const id of get(model.childIds(parentId))) {
        // A cycle in the model would recurse forever; the path already names every ancestor.
        if (parentPath.includes(id)) {
          continue;
        }
        const item = get(model.item(id));
        if (!item) {
          continue;
        }
        const path = [...parentPath, id];
        const props = get(model.itemProps(path));
        const value = Path.create(...path);
        const depth = parentIndexPath.length + 1;
        if (props.disposition === 'group') {
          const header: TreeNode<T> = {
            id,
            value,
            path,
            depth,
            item,
            props,
            branch: false,
            empty: false,
            open: true,
            current: false,
            group: true,
            last: false,
            disabled: true,
            indexPath: [],
          };
          const at = rows.length;
          const before = siblings.length;
          rows.push(header);
          walk(id, path, parentIndexPath, siblings);
          // An empty group renders nothing, so no header is left without rows.
          if (siblings.length === before) {
            rows.splice(at, 1);
          }
          continue;
        }

        // A node that can parent is a branch even before its children load (the app graph reports `parentOf: []` until
        // then), so it can be opened, which is what loads them.
        const branch = props.parentOf !== undefined;
        const open = branch && get(model.itemOpen(path));
        const node: TreeNode<T> = {
          id,
          value,
          path,
          depth,
          item,
          props,
          branch,
          empty: false,
          open,
          current: get(model.itemCurrent(path)),
          group: false,
          last: false,
          disabled: !!props.disabled,
          childrenCount: branch ? props.parentOf?.length : undefined,
          indexPath: [...parentIndexPath, siblings.length],
        };
        siblings.push(node);
        rows.push(node);
        byValue.set(node.value, node);
        node.current && selected.push(node.value);
        if (open) {
          expanded.push(node.value);
          node.children = walkChildren(id, path, node.indexPath);
          node.empty = node.children.length === 0;
        }
      }
    };

    const walkChildren = (parentId: string | undefined, parentPath: string[], parentIndexPath: number[]) => {
      const children: TreeNode<T>[] = [];
      walk(parentId, parentPath, parentIndexPath, children);
      const last = children.at(-1);
      if (last) {
        last.last = true;
      }
      return children;
    };

    const children = walkChildren(rootId, rootPath, []);
    const root: TreeNode<T> = {
      id: rootId ?? '',
      value: Path.create(...rootPath),
      path: rootPath,
      depth: 0,
      props: { id: rootId ?? '', label: '' },
      branch: true,
      empty: false,
      open: true,
      current: false,
      group: false,
      last: true,
      disabled: false,
      children,
      indexPath: [],
    };
    const rowIndex = new Map(rows.map((row, index) => [row.value, index]));
    return { root, rows, rowIndex, expanded, selected, byValue };
  });

/** A row's label as typeahead matches it; a translated label needs the caller's `toString`. */
const defaultNodeToString = (node: TreeNode) => (typeof node.props.label === 'string' ? node.props.label : node.id);

/** zag's collection over a walk; never asked for the children of a closed branch. */
export const createCollection = <T extends { id: string }>(
  root: TreeNode<T>,
  nodeToString: (node: TreeNode<T>) => string = defaultNodeToString,
) =>
  createTreeCollection<TreeNode<T>>({
    rootNode: root,
    nodeToValue: (node) => node.value,
    nodeToString,
    nodeToChildren: (node) => node.children ?? [],
    nodeToChildrenCount: (node) => node.childrenCount,
    isNodeDisabled: (node) => !!node.props.disabled,
  });
